import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Package, Search, Truck, CheckCircle, Clock, MapPin, ExternalLink, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORDER_STATUS_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'text-yellow-600 bg-yellow-100', icon: Clock },
  pagado: { label: 'Pagado', color: 'text-blue-600 bg-blue-100', icon: CheckCircle },
  procesando: { label: 'Procesando', color: 'text-black bg-neutral-100', icon: Package },
  enviado: { label: 'Enviado', color: 'text-green-600 bg-green-100', icon: Truck },
  entregado: { label: 'Entregado', color: 'text-green-700 bg-green-200', icon: MapPin },
  cancelado: { label: 'Cancelado', color: 'text-red-600 bg-red-100', icon: Clock }
};

const TrackOrderPage = () => {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      setError('Introduce el número de pedido');
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const response = await axios.get(`${API}/orders/track/${orderNumber.trim()}`);
      setOrderData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No se encontró ningún pedido con ese número');
      } else {
        setError('Error al buscar el pedido. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    return ORDER_STATUS_CONFIG[status] || ORDER_STATUS_CONFIG.pendiente;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la tienda
          </Button>

          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Package className="w-16 h-16 mx-auto mb-4 text-black" />
              <h1 className="text-3xl font-bold mb-2">Seguimiento de pedido</h1>
              <p className="text-gray-600">Introduce tu número de pedido para ver el estado del envío</p>
            </div>

            {/* Search Form */}
            <Card className="p-6 mb-6">
              <form onSubmit={handleSearch} className="flex gap-3">
                <Input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                  placeholder="Número de pedido (ej: JRF-20250210-A1B2C3D4)"
                  className="flex-1 uppercase"
                  data-testid="order-number-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-black hover:opacity-90"
                  data-testid="search-order-button"
                >
                  {loading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Buscar
                    </>
                  )}
                </Button>
              </form>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}
            </Card>

            {/* Order Status */}
            {orderData && (
              <Card className="p-6" data-testid="order-details">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Pedido</p>
                    <p className="font-mono font-bold text-lg">{orderData.order_number}</p>
                  </div>
                  <div className={`px-4 py-2 rounded-full flex items-center gap-2 ${getStatusConfig(orderData.status).color}`}>
                    {React.createElement(getStatusConfig(orderData.status).icon, { className: "w-4 h-4" })}
                    <span className="font-semibold">{getStatusConfig(orderData.status).label}</span>
                  </div>
                </div>

                {/* Shipping Carrier */}
                {orderData.shipping_carrier && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Truck className="w-6 h-6 text-black" />
                      <div>
                        <p className="font-semibold">{orderData.shipping_carrier.name}</p>
                        {orderData.shipping_carrier.delivery_time && (
                          <p className="text-sm text-gray-500">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {orderData.shipping_carrier.delivery_time}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tracking Number */}
                {orderData.tracking_number && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-700 mb-1">Número de seguimiento:</p>
                    <p className="font-mono font-bold text-green-800">{orderData.tracking_number}</p>
                    
                    {orderData.tracking_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-green-300 text-green-700 hover:bg-green-100"
                        onClick={() => window.open(orderData.tracking_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Seguir envío en {orderData.shipping_carrier?.name || 'transportista'}
                      </Button>
                    )}
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Fecha del pedido</p>
                    <p className="font-semibold">{formatDate(orderData.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Última actualización</p>
                    <p className="font-semibold">{formatDate(orderData.updated_at)}</p>
                  </div>
                </div>

                {/* Status Timeline */}
                <div className="mt-6 pt-6 border-t">
                  <p className="font-semibold mb-4">Estado del pedido</p>
                  <div className="space-y-3">
                    {['pagado', 'procesando', 'enviado', 'entregado'].map((status, index) => {
                      const isActive = ['pagado', 'procesando', 'enviado', 'entregado'].indexOf(orderData.status) >= index;
                      const isCurrent = orderData.status === status;
                      const config = getStatusConfig(status);
                      
                      return (
                        <div 
                          key={status} 
                          className={`flex items-center gap-3 ${!isActive && 'opacity-40'}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isActive ? config.color : 'bg-gray-100 text-gray-400'
                          } ${isCurrent ? 'ring-2 ring-offset-2 ring-pink-400' : ''}`}>
                            {React.createElement(config.icon, { className: "w-4 h-4" })}
                          </div>
                          <span className={`font-medium ${isActive ? 'text-gray-800' : 'text-gray-400'}`}>
                            {config.label}
                          </span>
                          {isCurrent && (
                            <span className="text-xs bg-neutral-100 text-black px-2 py-1 rounded-full">
                              Actual
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TrackOrderPage;
